/**
 * Elevation requests — the admin home for time-boxed access. "Request access"
 * sits in the page header; the body is the requests (waiting, active, history,
 * and the audit trail) and, for a tenant administrator, a second tab naming
 * the directory groups whose members approve.
 *
 * Approval authority can come from a directory group, which carries no
 * permission a gate could read, so who may decide is taken from `tenantWide` on
 * the list response and never re-derived from the permission set.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SectionActions, { SECTION_BUTTON } from "../components/settings/SectionActions.jsx";
import { PAGE_PART } from "../components/settings/Page.jsx";
import EmptyState from "../components/EmptyState.jsx";
import Button from "../components/ui/Button.jsx";
import Notice from "../components/ui/Notice.jsx";
import Tabs from "../components/ui/Tabs.jsx";
import Chevron from "../components/ui/Chevron.jsx";
import { SkeletonBar } from "../components/ui/Skeleton.jsx";
import EscalationCard from "../components/escalation/EscalationCard.jsx";
import EscalationRequestDialog from "../components/escalation/EscalationRequestDialog.jsx";
import ApproverGroupsPanel from "../components/escalation/ApproverGroupsPanel.jsx";
import EscalationAuditTab from "../components/escalation/EscalationAuditTab.jsx";
import { hasPermission, invalidateMyPermissions, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialOrRawMessage } from "../lib/denialReason.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { useEndpointNames, useSubjectNames } from "../lib/escalationTargets.js";
import {
    ESCALATION_STATES,
    canRequestEscalation,
    cancelEscalationRequest,
    decideEscalationRequest,
    isLiveEscalation,
    listEscalationRequests,
    revokeEscalationRequest,
} from "../lib/escalationClient.js";

const REQUESTS_PER_PAGE = 5;
const VIEWS = [{ id: "requests", label: "Requests" }, { id: "approvers", label: "Approver groups" }];

// A runaway guard, not a product limit. The queues are bounded in practice:
// live windows are capped per analyst, and pending rows leave the queue as they
// are decided.
const QUEUE_PAGE_CAP = 20;

// An approver cannot decide or revoke a row the page never fetched, so the two
// queues drain instead of showing their first page. History is unbounded and
// pages on demand.
async function listQueue(states) {
    const requests = [];
    let pageToken = "";
    for (let page = 0; page < QUEUE_PAGE_CAP; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await listEscalationRequests({ states, pageToken });
        requests.push(...result.requests);
        pageToken = result.nextPageToken;
        if (!pageToken) break;
    }
    return requests;
}

// The three calls overlap: the unfiltered history page carries pending and live
// rows too. Newest first, matching the order every queue renders in.
function mergeById(requests) {
    const byId = new Map();
    for (const request of requests) {
        byId.set(request.id, request);
    }
    return [...byId.values()].sort(
        (a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0),
    );
}

function Section({ title, requests, cardProps, hasMore = false, loadingMore = false, onLoadMore }) {
    const sectionRef = useRef(null);
    const [page, setPage] = useState(0);
    const lastPage = Math.max(0, Math.ceil(requests.length / REQUESTS_PER_PAGE) - 1);
    const currentPage = Math.min(page, lastPage);
    const start = currentPage * REQUESTS_PER_PAGE;
    const end = Math.min(start + REQUESTS_PER_PAGE, requests.length);
    const count = `${requests.length}${hasMore ? "+" : ""}`;

    useEffect(() => { setPage((current) => Math.min(current, lastPage)); }, [lastPage]);

    const changePage = (nextPage) => {
        setPage(nextPage);
        sectionRef.current?.scrollIntoView({ block: "start" });
    };

    return (
        <section ref={sectionRef} aria-label={title} aria-busy={loadingMore ? "true" : undefined} className="grid min-w-0 scroll-mt-[12px] gap-[10px]">
            <h3 className={PAGE_PART}>
                {title} <span className="font-normal tabular-nums text-[color:var(--muted)]">({count})</span>
            </h3>
            <div className="grid min-w-0 gap-[8px]">
                {requests.slice(start, end).map((request) => (
                    <EscalationCard key={request.id} {...cardProps(request)} />
                ))}
            </div>
            {requests.length > REQUESTS_PER_PAGE || hasMore ? (
                <nav aria-label={`${title} pagination`} className="flex flex-wrap items-center justify-between gap-[8px]">
                    <span aria-live="polite" aria-atomic="true" className="text-[12px] tabular-nums text-[color:var(--muted)]">
                        {requests.length ? `${start + 1}-${end} of ${count}` : ""}
                    </span>
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-[6px]">
                        <Button size="sm" disabled={currentPage === 0 || loadingMore} onClick={() => changePage(currentPage - 1)}>
                            Previous
                        </Button>
                        <Button size="sm" disabled={currentPage === lastPage || loadingMore} onClick={() => changePage(currentPage + 1)}>
                            Next
                        </Button>
                        {hasMore && currentPage === lastPage ? (
                            <Button size="sm" disabled={loadingMore} onClick={onLoadMore}>
                                {loadingMore ? "Loading..." : "Load earlier requests"}
                            </Button>
                        ) : null}
                    </div>
                </nav>
            ) : null}
        </section>
    );
}

// First load: one list in the shape the lists render in (a heading over
// request cards), so the page doesn't build itself from nothing.
function SectionLoading() {
    return (
        <div className="grid min-w-0 gap-[10px]">
            <span className="sr-only">Loading access requests…</span>
            <div aria-hidden="true" className="flex h-[20px] items-center">
                <SkeletonBar className="w-[120px]" />
            </div>
            {Array.from({ length: 3 }, (_, i) => (
                <div
                    key={i}
                    aria-hidden="true"
                    className="grid gap-[10px] rounded-[10px] border border-[color:var(--line)] p-[16px]"
                    style={{ opacity: Math.max(0.25, 1 - i * 0.12) }}
                >
                    <SkeletonBar className="w-[40%]" />
                    <SkeletonBar className="w-[72%]" />
                    <SkeletonBar className="w-[54%]" />
                </div>
            ))}
        </div>
    );
}

export default function EscalationsPage() {
    const caps = useMyPermissions();
    const user = useCurrentUser();
    // Naming an approver group hands approval to everyone in it, so configuring
    // the rota is tenant administration, not something an approver can do.
    const canManageApprovers = hasPermission(caps, PERMISSIONS.ROLES_MANAGE);
    const canRequest = canRequestEscalation(caps);

    const [requests, setRequests] = useState([]);
    // null until the list answers: rendering either audience before then makes
    // the header claim a scope, and "Your requests" flickers on every mount.
    const [tenantWide, setTenantWide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [historyCursor, setHistoryCursor] = useState("");
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [view, setView] = useState(VIEWS[0].id);
    const [acting, setActing] = useState({});

    // Errors are held raw and turned into copy at render: threading `caps`
    // through the callbacks would rebuild them whenever the posture object
    // changes identity, and `refresh` runs in an effect keyed on itself.
    //
    // The two queues a person acts on are fetched by state, not sliced out of
    // the newest page: an older pending or still-live escalation would
    // otherwise fall off the end once enough newer requests existed, taking
    // approval and early revocation with it. The unfiltered call is only the
    // first page of history, which `loadMore` walks.
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [pendingRows, approvedRows, historyPage] = await Promise.all([
                listQueue([ESCALATION_STATES.PENDING]),
                listQueue([ESCALATION_STATES.APPROVED]),
                listEscalationRequests(),
            ]);
            setRequests(mergeById([
                ...pendingRows,
                ...approvedRows,
                ...historyPage.requests,
            ]));
            setHistoryCursor(historyPage.nextPageToken);
            setTenantWide(historyPage.tenantWide);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (!historyCursor) return;
        setLoadingMore(true);
        try {
            const page = await listEscalationRequests({ pageToken: historyCursor });
            setRequests((current) => mergeById([...current, ...page.requests]));
            setHistoryCursor(page.nextPageToken);
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoadingMore(false);
        }
    }, [historyCursor]);

    useEffect(() => { refresh(); }, [refresh]);

    const applyResult = useCallback((updated) => {
        setRequests((current) => current.map((r) => (r.id === updated.id ? updated : r)));
        invalidateMyPermissions();
    }, []);

    const runAction = useCallback(async (requestId, work) => {
        setActing((current) => ({ ...current, [requestId]: "pending" }));
        try {
            applyResult(await work());
            setActing((current) => {
                const { [requestId]: _done, ...rest } = current;
                return rest;
            });
        } catch (err) {
            setActing((current) => ({ ...current, [requestId]: err }));
        }
    }, [applyResult]);

    const endpointIds = useMemo(
        () => requests.map((r) => r.target.endpointId).filter(Boolean),
        [requests],
    );
    const endpointNames = useEndpointNames(
        endpointIds,
        hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ),
    );
    const subjects = useMemo(
        () => requests.map((r) => r.target.subject).filter(Boolean),
        [requests],
    );
    const subjectNames = useSubjectNames(
        subjects,
        hasPermission(caps, PERMISSIONS.DIRECTORY_DATA_READ),
    );

    const { live, pending, history } = useMemo(() => {
        const now = Date.now();
        return {
            live: requests.filter((r) => isLiveEscalation(r, now)),
            pending: requests.filter((r) => r.state === ESCALATION_STATES.PENDING),
            history: requests.filter(
                (r) => r.state !== ESCALATION_STATES.PENDING && !isLiveEscalation(r, now),
            ),
        };
    }, [requests]);

    const cardProps = useCallback((request) => ({
        request,
        endpointNames,
        subjectNames,
        isOwn: Boolean(user?.userId) && request.requesterUserId === user.userId,
        canDecide: tenantWide === true,
        busy: acting[request.id] === "pending",
        error: acting[request.id] && acting[request.id] !== "pending"
            ? denialOrRawMessage(acting[request.id], caps)
            : null,
        onDecide: (decision) => runAction(request.id, () => decideEscalationRequest({
            requestId: request.id, ...decision,
        })),
        onCancel: () => runAction(request.id, () => cancelEscalationRequest(request.id)),
        onRevoke: () => runAction(request.id, () => revokeEscalationRequest(request.id)),
    }), [endpointNames, subjectNames, user, tenantWide, acting, runAction, caps]);

    const requestsView = (
        <div aria-busy={loading ? "true" : undefined} className="grid min-w-0 gap-[24px]">
            {error ? <Notice tone="error">{denialOrRawMessage(error, caps)}</Notice> : null}

            {loading && !requests.length ? <SectionLoading /> : null}

            {pending.length ? (
                <Section title="Waiting" requests={pending} cardProps={cardProps} />
            ) : null}

            {live.length ? (
                <Section title="Active access" requests={live} cardProps={cardProps} />
            ) : null}

            {history.length || historyCursor ? (
                <Section
                    title="History"
                    requests={history}
                    cardProps={cardProps}
                    hasMore={Boolean(historyCursor)}
                    loadingMore={loadingMore}
                    onLoadMore={loadMore}
                />
            ) : null}

            {!loading && !error && !requests.length ? (
                <EmptyState
                    title="No access requests"
                    note={canRequest ? "Use “Request access” to ask for temporary access to one person's or machine's activity." : null}
                />
            ) : null}

            {/* The trail is only readable by an approver, and `tenantWide` is the
                one signal that covers a directory-group approver too. */}
            {tenantWide === true && requests.length ? (
                <details className="group/audit min-w-0">
                    <summary className="flex w-fit cursor-pointer list-none items-center gap-[6px] rounded-[6px] text-[13px] text-[color:var(--muted)] hover:text-[color:var(--ink)] [&::-webkit-details-marker]:hidden">
                        <span className="inline-flex transition-transform group-open/audit:rotate-90"><Chevron /></span>Audit trail
                    </summary>
                    <div className="pt-[10px]">
                        <EscalationAuditTab requests={requests} endpointNames={endpointNames} subjectNames={subjectNames} />
                    </div>
                </details>
            ) : null}
        </div>
    );

    return (
        <>
            {canRequest ? (
                <SectionActions>
                    <button type="button" onClick={() => setDialogOpen(true)} className={SECTION_BUTTON}>Request access</button>
                </SectionActions>
            ) : null}
            {canManageApprovers ? (
                <div className="flex min-w-0 flex-col gap-[16px]">
                    <Tabs items={VIEWS} value={view} onChange={setView} label="Elevation requests" className="self-start" />
                    {view === "approvers" ? (
                        <section aria-label="Who approves" className="min-w-0">
                            <ApproverGroupsPanel />
                        </section>
                    ) : requestsView}
                </div>
            ) : requestsView}

            <EscalationRequestDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmitted={refresh}
            />
        </>
    );
}
